// Single configuration system for HappyFamilyAI
// Handles both build-time injection and runtime environment detection

interface FrontendConfig {
  API_BASE: string;
  WS_BASE: string;
  APP_VERSION: string;
  GIT_SHA: string;
  BUILD_TIME: string;
  BANNER_COLOR_START?: string;
  BANNER_COLOR_END?: string;
  ENVIRONMENT?: string;
  DEPLOYED_AT?: string;
}

// Build-time config (version info)
interface BuildConfig {
  API_BASE?: string;
  WS_BASE?: string;
  APP_VERSION?: string;
  GIT_SHA?: string;
  BUILD_TIME?: string;
  BANNER_COLOR_START?: string;
  BANNER_COLOR_END?: string;
  ENVIRONMENT?: string;
  DEPLOYED_AT?: string;
}

// Deploy-time config (environment info)
interface EnvConfig {
  API_BASE?: string;
  WS_BASE?: string;
  ENVIRONMENT?: string;
  DEPLOYED_AT?: string;
}

declare global {
  interface Window {
    FRONTEND_CONFIG?: BuildConfig;
    ENV_CONFIG?: EnvConfig;
  }
}

let frontendConfig: FrontendConfig | null = null;

type BuildEnv = {
  appVersion: string;
  gitSha: string;
  buildTime: string;
  environment: string;
  environmentName: string;
  isProductionBuild: boolean;
  debugEnabled: boolean;
};

function getBuildEnv(): BuildEnv {
  const metaEnv = (import.meta as any).env ?? {};
  const testEnv = (globalThis as any).__VITE_ENV__ ?? {};
  const getValue = (key: string) => metaEnv[key] ?? testEnv[key];

  const appVersion = getValue('VITE_APP_VERSION') ?? '1.0.0';
  const gitSha = getValue('VITE_GIT_COMMIT') ?? 'unknown';
  const buildTime = getValue('VITE_BUILD_TIME') ?? new Date().toISOString();
  const environment = getValue('VITE_ENV') ?? 'development';
  const environmentName = getValue('VITE_ENVIRONMENT_NAME') ?? 'dev';
  const isProductionBuild = (getValue('VITE_IS_PRODUCTION_BUILD') ?? 'false') === 'true';
  const debugEnabled = (getValue('VITE_DEBUG') ?? 'false') === 'true';

  return {
    appVersion,
    gitSha,
    buildTime,
    environment,
    environmentName,
    isProductionBuild,
    debugEnabled,
  };
}

export function getConfig(): FrontendConfig {
  if (!frontendConfig) {
    // Initialize config if not already loaded
    frontendConfig = loadConfig();
  }
  return frontendConfig;
}

function loadConfig(): FrontendConfig {
  const hostname = window.location.hostname;
  const buildEnv = getBuildEnv();
  
  // Read Vite environment variables first (highest priority for local dev)
  const viteApiBase = (import.meta as any).env?.VITE_API_BASE;
  const viteWsBase = (import.meta as any).env?.VITE_WS_BASE;
  
  // Merge build-time config (version info) with deploy-time config (environment)
  const buildConfig = window.FRONTEND_CONFIG || {};
  const envConfig = window.ENV_CONFIG || {};
  
  // Priority: Vite env vars (dev) > deploy-time config (runtime) > build-time config (build)
  const apiBase = viteApiBase || envConfig.API_BASE || buildConfig.API_BASE;
  const wsBase = viteWsBase || envConfig.WS_BASE || buildConfig.WS_BASE;
  
  // No fallbacks - ports must be explicitly set via environment variables
  if (!apiBase) {
    throw new Error('API_BASE environment variable is required');
  }
  if (!wsBase) {
    throw new Error('WS_BASE environment variable is required');
  }
  
  return {
    API_BASE: apiBase,
    WS_BASE: wsBase,
    APP_VERSION: buildConfig.APP_VERSION || buildEnv.appVersion,
    GIT_SHA: buildConfig.GIT_SHA || buildEnv.gitSha,
    BUILD_TIME: buildConfig.BUILD_TIME || buildEnv.buildTime,
    BANNER_COLOR_START: buildConfig.BANNER_COLOR_START || '#272823',
    BANNER_COLOR_END: buildConfig.BANNER_COLOR_END || '#32332E',
    ENVIRONMENT: envConfig.ENVIRONMENT || buildEnv.environment,
    DEPLOYED_AT: envConfig.DEPLOYED_AT || buildEnv.buildTime
  };
}

export function resetConfigForTests(): void {
  frontendConfig = null;
}

// Export convenience getters for common use cases
export const appConfig = {
  // Build-time values
  APP_NAME: 'HappyFamilyAI',
  VERSION: getBuildEnv().appVersion,
  GIT_COMMIT: getBuildEnv().gitSha,
  BUILD_TIMESTAMP: getBuildEnv().buildTime,
  BUILD_MODE: getBuildEnv().environment,
  ENVIRONMENT_NAME: getBuildEnv().environmentName,
  IS_PRODUCTION_BUILD: getBuildEnv().isProductionBuild,
  
  // Runtime values (from getConfig())
  get API_BASE() { return getConfig().API_BASE; },
  get WS_BASE() { return getConfig().WS_BASE; },
  get APP_VERSION() { return getConfig().APP_VERSION; },
  get GIT_SHA() { return getConfig().GIT_SHA; },
  get BUILD_TIME() { return getConfig().BUILD_TIME; },
  get ENVIRONMENT() { return getConfig().ENVIRONMENT; },
  
  // Feature flags
  ENABLE_DEBUG: getBuildEnv().debugEnabled && !getBuildEnv().isProductionBuild,
  ENABLE_WEBSOCKET: import.meta.env.VITE_ENABLE_WEBSOCKET !== 'false',
  ENABLE_VERBOSE_LOGGING: !getBuildEnv().isProductionBuild,
  ENABLE_DEVTOOLS: !getBuildEnv().isProductionBuild,
  
  // Environment detection
  IS_PRODUCTION: import.meta.env.MODE === 'production',
  IS_STAGING: import.meta.env.MODE === 'staging',
  IS_DEVELOPMENT: import.meta.env.MODE === 'development',
};

export default appConfig;
